import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import WorkWithUs from "../components/sections/WorkWithUs";

const PAYMENT_STATUS_SNAPSHOT_KEY = "orbi_payment_status_v1";

function readPaymentSnapshot() {
  try {
    const raw = sessionStorage.getItem(PAYMENT_STATUS_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePaymentSnapshot(data) {
  try {
    sessionStorage.setItem(PAYMENT_STATUS_SNAPSHOT_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export default function PaymentStatus() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Prefer live URL params; fall back to last snapshot so refresh / tab restore still works
  // after we clean the query string from the address bar.
  const initialFromUrl = {
    status: searchParams.get("status"),
    orderId: searchParams.get("order_id"),
    bookingCode: searchParams.get("booking_code"),
  };
  const snapshot = !initialFromUrl.orderId && !initialFromUrl.status ? readPaymentSnapshot() : null;
  const initial = {
    status: initialFromUrl.status || snapshot?.status || null,
    orderId: initialFromUrl.orderId || snapshot?.orderId || null,
    bookingCode: initialFromUrl.bookingCode || snapshot?.bookingCode || "",
  };

  const [status] = useState(initial.status);
  const [orderId] = useState(initial.orderId);
  const [bookingCode] = useState(initial.bookingCode);
  const [resolvedStatus, setResolvedStatus] = useState(status || (orderId ? "processing" : "expired"));
  const [resolvedBookingCode, setResolvedBookingCode] = useState(bookingCode || "");
  const [isResolving, setIsResolving] = useState(Boolean(orderId));

  useEffect(() => {
    // Persist before stripping URL so a later refresh / tab restore can recover context
    if (orderId || status || bookingCode) {
      writePaymentSnapshot({
        orderId: orderId || null,
        status: status || null,
        bookingCode: bookingCode || "",
      });
    }

    // Remove query parameters from URL without refreshing
    window.history.replaceState(null, "", window.location.pathname);
    // Clear any pending booking data — the booking flow is complete
    localStorage.removeItem("pendingBooking");
    // Clear checkout session snapshots so next checkout doesn't show stale data
    try {
      sessionStorage.removeItem("orbi_checkout_booking_v1");
      sessionStorage.removeItem("orbi_booking_draft");
      sessionStorage.removeItem("orbi_active_checkout_booking_v1");
    } catch (_) {
      /* ignore */
    }
  }, [orderId, status, bookingCode]);

  useEffect(() => {
    let isMounted = true;
    const resolveFromBackend = async () => {
      if (!orderId) {
        setIsResolving(false);
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_APP_DOMAIN}/api/payment-status?order_id=${encodeURIComponent(orderId)}`,
        );
        const payload = res?.data || {};
        const normalized = String(payload.status || "").toUpperCase();
        if (!isMounted) return;

        let nextStatus = "processing";
        if (normalized === "PAID") {
          nextStatus = "success";
        } else if (normalized === "FAILED") {
          nextStatus = "failed";
        } else if (status) {
          nextStatus = status;
        }

        setResolvedStatus(nextStatus);
        const nextBookingCode = resolvedBookingCode || payload.booking_code || "";
        if (!resolvedBookingCode && payload.booking_code) {
          setResolvedBookingCode(payload.booking_code);
        }
        writePaymentSnapshot({
          orderId,
          status: nextStatus,
          bookingCode: nextBookingCode,
        });
      } catch (error) {
        if (isMounted) {
          setResolvedStatus(status || "processing");
        }
      } finally {
        if (isMounted) {
          setIsResolving(false);
        }
      }
    };
    resolveFromBackend();
    return () => {
      isMounted = false;
    };
  }, [orderId, resolvedBookingCode, status]);

  const isSuccess = resolvedStatus === "success";
  const isFailed = resolvedStatus === "failed";
  const isExpired = resolvedStatus === "expired";
  const isPending = !isSuccess && !isFailed && !isExpired;
  const isRemainingPayment =
    isSuccess &&
    typeof orderId === "string" &&
    orderId.toLowerCase().includes("_remaining_");

  const navigateIfLoggedIn = (path) => {
    const token = localStorage.getItem("user_token");
    if (!token) {
      navigate("/login");
      return;
    }
    navigate(path);
  };

  return (
    <>
      <section className="payment_status_wrap section_pb">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8 text-center mt-5">
              <div
                className={`status_card p-5 shadow-sm rounded-4 ${
                  isSuccess
                    ? "bg-success-subtle"
                    : isFailed
                      ? "bg-danger-subtle"
                      : isExpired
                        ? "bg-light"
                        : "bg-warning-subtle"
                }`}
              >
                <div className="status_icon mb-4">
                  {isSuccess ? (
                    <i
                      className="bi bi-check-circle-fill text-success"
                      style={{ fontSize: "5rem" }}
                    ></i>
                  ) : isPending ? (
                    <i
                      className="bi bi-hourglass-split text-warning"
                      style={{ fontSize: "5rem" }}
                    ></i>
                  ) : isExpired ? (
                    <i
                      className="bi bi-info-circle-fill text-secondary"
                      style={{ fontSize: "5rem" }}
                    ></i>
                  ) : (
                    <i
                      className="bi bi-x-circle-fill text-danger"
                      style={{ fontSize: "5rem" }}
                    ></i>
                  )}
                </div>

                <h1
                  className={`mb-3 fw-bold ${
                    isSuccess
                      ? "text-success"
                      : isFailed
                        ? "text-danger"
                        : isExpired
                          ? "text-secondary"
                          : "text-warning"
                  }`}
                >
                  {isSuccess
                    ? isRemainingPayment
                      ? "Remaining Payment Successful"
                      : "Booking Confirmed"
                    : isPending
                      ? "Payment Processing"
                      : isExpired
                        ? "Payment Session Expired"
                        : "Payment Failed"}
                </h1>

                <p className="lead mb-4">
                  {isSuccess
                    ? isRemainingPayment
                      ? "Thank you for choosing Orbi. Your remaining amount has been paid successfully."
                      : "Thank you for choosing Orbi, Your Booking has been confirmed."
                    : isPending
                      ? "We are still confirming your payment with the gateway. Please check My Bookings in a moment."
                      : isExpired
                        ? "This payment confirmation page is no longer available. If your payment was completed, your booking is safe — please check My Bookings."
                        : "Something went wrong with your payment. Please try again or contact support if the issue persists."}
                </p>
                {isResolving && (
                  <p className="mb-3 text-muted">Confirming latest payment status...</p>
                )}

                {!isExpired && (
                  <div
                    className="order_details p-3 rounded-3 mb-4 text-start mx-auto"
                    style={{ maxWidth: "400px" }}
                  >
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-dark">Booking Id</span>
                      <span className="fw-medium font-monospace">{resolvedBookingCode || "-"}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-dark">Status:</span>
                      <span
                        className={`fw-bold ${isSuccess ? "text-success" : isFailed ? "text-danger" : "text-warning"}`}
                      >
                        {isSuccess ? "CONFIRMED" : isPending ? "PROCESSING" : "FAILED / CANCELLED"}
                      </span>
                    </div>
                  </div>
                )}

                <div className="d-grid gap-3 d-sm-flex justify-content-center">
                  <button
                    onClick={() => navigateIfLoggedIn("/bookings")}
                    className="btn btn-warning px-4 py-2 fw-bold"
                  >
                    Go to My Bookings
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <WorkWithUs />
    </>
  );
}
